import * as Sentry from '@sentry/nextjs';
import Router from 'next/router';
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys';
import { isStorageFallbackActive } from '@/utils/storageFallback';

const ATTEMPT_COUNT_KEY = SESSION_STORAGE_KEYS.CHUNK_LOAD_RELOAD_COUNT;
const MAX_RELOAD_ATTEMPTS = 1;

/**
 * The reload counter in sessionStorage, or `null` when sessionStorage cannot hold it: the browser
 * blocks it, or the in-memory fallback (TEAM-14430) is active, which empties on every reload.
 */
function readAttemptCount(): number | null {
  if (isStorageFallbackActive('sessionStorage')) return null;
  try {
    return parseInt(sessionStorage.getItem(ATTEMPT_COUNT_KEY) ?? '0', 10) || 0;
  } catch {
    return null;
  }
}

/**
 * Without sessionStorage, the one reload is remembered in `window.name`, which survives a reload of
 * the same tab (TEAM-15666). The marker names the path the reload lands on and when it was made, so
 * a later failure on that path within {@link RELOAD_MARKER_TTL_MS} does not reload again — no loop.
 * The same marker covers sessionStorage that can be read but not written, such as a full quota.
 * A `window.name` that holds anything else is left alone and the reload is treated as spent.
 */
const RELOAD_MARKER_PREFIX = 'coo_chunk_reload:';
export const RELOAD_MARKER_TTL_MS = 5 * 60 * 1000;

function readReloadMarker(): { path: string; at: number } | null {
  if (!window.name.startsWith(RELOAD_MARKER_PREFIX)) return null;
  const rest = window.name.slice(RELOAD_MARKER_PREFIX.length);
  const separator = rest.lastIndexOf(':');
  const at = Number(rest.slice(separator + 1));
  if (separator < 0 || !Number.isFinite(at)) return null;
  return { path: rest.slice(0, separator), at };
}

/** Some other script owns window.name: it is never overwritten, so no reload can be recorded there. */
function isWindowNameForeign(): boolean {
  return window.name !== '' && !window.name.startsWith(RELOAD_MARKER_PREFIX);
}

function hasReloadMarker(path: string): boolean {
  const marker = readReloadMarker();
  return marker !== null && marker.path === path && Date.now() - marker.at < RELOAD_MARKER_TTL_MS;
}

function hasExhaustedReloads(path: string): boolean {
  const count = readAttemptCount();
  if (count === null) return isWindowNameForeign() || hasReloadMarker(path);
  // The marker also counts here: it is where the reload went when the counter could not be written
  return count >= MAX_RELOAD_ATTEMPTS || hasReloadMarker(path);
}

/**
 * Records the reload about to happen. Returns false when nothing could record it — sessionStorage
 * refused the write and window.name belongs to another script — because a reload nobody remembers
 * would repeat on every load.
 */
function recordReload(path: string): boolean {
  const count = readAttemptCount();
  if (count !== null) {
    try {
      sessionStorage.setItem(ATTEMPT_COUNT_KEY, String(count + 1));
      return true;
    } catch {
      // Readable but not writable (a full quota): the marker records it instead
    }
  }
  if (isWindowNameForeign()) return false;
  window.name = `${RELOAD_MARKER_PREFIX}${path}:${Date.now()}`;
  return true;
}

/** The path a reload or a hard navigation to `destinationUrl` lands on. */
function landingPath(destinationUrl?: string): string {
  if (!destinationUrl) return window.location.pathname;
  try {
    return new URL(destinationUrl, window.location.href).pathname;
  } catch {
    return window.location.pathname;
  }
}

/**
 * How long a page file the page needs waits before it is requested once more (TEAM-15666). A failed
 * request on a fresh load of the current build is usually a network blip; the retry spares the diner
 * a reload. Only when the retry fails too does the reload path run.
 */
export const CHUNK_RETRY_DELAY_MS = 1000;

/**
 * How long a page load whose first chunk failure is an HTML page run as a script waits for the rest
 * of the burst before it is reported. One stale page fails several chunks at once — production
 * breadcrumbs show 8 SyntaxErrors inside 250 ms (TEAM-15331) — and they are folded into a single
 * report. Only that shape waits: see {@link handleFailure}.
 */
export const REPORT_COLLECTION_WINDOW_MS = 500;

/**
 * A chunk the browser executed while its body was an HTML page — a superseded build file answered
 * with a web page, which throws a bare `SyntaxError: Unexpected token '<'` (COO-WEBAPP-180,
 * TEAM-15331). Replaces that SyntaxError in Sentry so the report is named and carries the failed
 * chunk URL. The browser's SyntaxError stays attached as `cause`.
 *
 * Sentry drops custom error properties, so `url`, `urls` and `count` are also sent as the
 * `chunk_load_failure` context (`failed_url`, `failed_urls`, `failure_count`).
 */
export class StaleChunkError extends Error {
  readonly url: string;

  readonly urls: string[];

  constructor(urls: string[], cause: SyntaxError) {
    super(`Stale chunk returned HTML instead of JavaScript: ${urls[0]}`);
    this.name = 'StaleChunkError';
    this.url = urls[0];
    // A copy: the load's report keeps collecting URLs after this error has been captured.
    this.urls = [...urls];
    // Assigned rather than passed to super: the ErrorOptions argument is ignored by browsers that
    // predate it (same pattern as NonJsonResponseError).
    (this as { cause?: unknown }).cause = cause;
  }

  get count(): number {
    return this.urls.length;
  }
}

/**
 * The browser SyntaxErrors this handler recognised as stale chunks. Sentry's global onerror handler
 * still builds its own event for each of them; {@link dropHandledChunkErrors} drops it in
 * `beforeSend`, because the per-load report already covers it.
 */
const handledChunkErrors = new WeakSet();

/**
 * Sentry `beforeSend` hook: drops the bare event Sentry's global onerror handler builds for a
 * stale-chunk SyntaxError this handler already reported (TEAM-15331). Every other event passes
 * through unchanged.
 */
export function dropHandledChunkErrors<T>(
  event: T,
  hint?: { originalException?: unknown },
): T | null {
  const error = hint?.originalException;
  if (typeof error === 'object' && error !== null && handledChunkErrors.has(error)) return null;
  return event;
}

interface LoadReport {
  failedUrls: string[];
  originalError?: Error;
  willReload: boolean;
  /** The first failure was a page file whose one retry also failed (TEAM-15666). */
  retried: boolean;
}

/** Groups every chunk failure that reached the diner's page under one Sentry issue (COO-WEBAPP-4ZF). */
const CHUNK_LOAD_FINGERPRINT = 'chunk_load_failure';

/**
 * Groups failed background prefetches under their own Sentry issue: the diner's page was unaffected,
 * so they stay out of the volume and catch rate read from COO-WEBAPP-4ZF (TEAM-15666).
 */
const CHUNK_PREFETCH_FINGERPRINT = 'chunk_prefetch_failure';

/**
 * Sends one chunk-load event. Every event shares the `chunk_load_failure` tag and context; `tags`
 * are set in order after `error_class`.
 */
function captureChunkEvent(
  level: 'error' | 'warning',
  tags: Record<string, string>,
  urls: string[],
  error: Error,
  fingerprint = CHUNK_LOAD_FINGERPRINT,
): void {
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag('error_class', 'chunk_load_failure');
    Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
    scope.setContext('chunk_load_failure', {
      failed_url: urls[0],
      // A copy, for the same reason as StaleChunkError.urls
      failed_urls: [...urls],
      failure_count: urls.length,
      page_url: window.location.href,
    });
    // Explicit fingerprint so the failures group under one Sentry issue
    // regardless of the build-specific content hash in the URL.
    scope.setFingerprint([fingerprint]);
    Sentry.captureException(error);
  });
}

function reportToSentry({ failedUrls, originalError, willReload, retried }: LoadReport): void {
  const error =
    originalError instanceof SyntaxError
      ? new StaleChunkError(failedUrls, originalError)
      : originalError ?? new Error(`Chunk load failure: ${failedUrls[0]}`);
  captureChunkEvent(
    'error',
    { will_reload: String(willReload), ...(retried ? { retry: 'failed' } : {}) },
    failedUrls,
    error,
  );
}

// The report for this document load, once a failure has been handled.
// Multiple <script> tags can fail in the same page (webpack, framework, main, page chunk),
// but we only want to count and reload once per load — otherwise a single incident
// exhausts the entire budget and inflates Sentry event counts (TEAM-14340 run 5).
// Later failures add their URL to this report instead of being sent on their own (TEAM-15331).
let loadReport: LoadReport | null = null;

interface FailureOptions {
  originalError?: Error;
  /** The router's intended destination: the reload hard-navigates there instead. */
  destinationUrl?: string;
  retried?: boolean;
}

function handleFailure(
  failedUrl: string,
  { originalError, destinationUrl, retried = false }: FailureOptions = {},
): void {
  if (loadReport) {
    loadReport.failedUrls.push(failedUrl);
    return;
  }

  const path = landingPath(destinationUrl);
  const report: LoadReport = {
    failedUrls: [failedUrl],
    originalError,
    willReload: !hasExhaustedReloads(path) && recordReload(path),
    retried,
  };
  loadReport = report;

  const send = (): void => {
    reportToSentry(report);
    if (!report.willReload) return;

    // Flush Sentry before navigating — the page is about to be torn down
    // and the event may not survive without an explicit flush.
    // .catch prevents an unhandled rejection when the flush itself fails
    // (offline, transport down — the same conditions that break chunks).
    void Sentry.flush(2000)
      // eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional swallow
      .catch(() => {})
      .then(() => {
        if (destinationUrl) {
          // Hard-navigate to the intended destination so the diner lands on the
          // correct page (e.g. order-confirmation) with fresh assets.
          window.location.assign(destinationUrl);
        } else {
          window.location.reload();
        }
      });
  };

  // An HTML page run as a script (SyntaxError) waits for the rest of its burst, so the load is
  // reported once with every failed URL; Next only gives up on that route after its ~3.8 s timeout.
  // A chunk that failed to load, or a router failure, is reported at once: Next hard-navigates
  // immediately on an asset error (handleRouteInfoError in next/dist/shared/lib/router/router.js),
  // which would tear down a pending timer. Either way, a failure after the report went out joins a
  // report that has already been sent and is not counted.
  if (originalError instanceof SyntaxError) {
    setTimeout(send, REPORT_COLLECTION_WINDOW_MS);
  } else {
    send();
  }
}

// ── Retry of a page file the page needs (TEAM-15666) ──────────────────────────────────────────

/** Marks the <script> this handler appended to retry a failed page file. */
const RETRY_ATTRIBUTE = 'data-chunk-retry';

interface RetryState {
  pending: number;
  recoveredUrls: string[];
}

// The retries of this document load. Replaced, not mutated, on init so that a retry still in flight
// settles against the state it started with.
let retryState: RetryState = { pending: 0, recoveredUrls: [] };

/** One report for the load once every retry has settled and all of them loaded. */
function reportRecovered(state: RetryState): void {
  if (state.pending > 0 || state.recoveredUrls.length === 0 || loadReport) return;
  const urls = state.recoveredUrls;
  captureChunkEvent(
    'warning',
    { retry: 'recovered' },
    urls,
    new Error(`Chunk load recovered on retry: ${urls[0]}`),
  );
}

/**
 * How long a retry may stay unsettled before it counts as failed (TEAM-15666). A request that hangs
 * fires neither load nor error until the browser's own network timeout, which can take minutes, and
 * the page cannot hydrate meanwhile.
 */
export const CHUNK_RETRY_TIMEOUT_MS = 10000;

/**
 * Request a failed page file once more after {@link CHUNK_RETRY_DELAY_MS}. Webpack registers a chunk
 * whenever its script runs, so a late load lets the page carry on. If the retry fails, its error
 * reaches {@link onScriptError} and takes the reload path; if it has not settled within
 * {@link CHUNK_RETRY_TIMEOUT_MS}, it takes the same path.
 */
function scheduleRetry(failed: HTMLScriptElement, src: string): void {
  const state = retryState;
  state.pending += 1;
  setTimeout(() => {
    // The load is already being reloaded or reported: the retry would change nothing
    if (loadReport) {
      state.pending -= 1;
      handleFailure(src);
      return;
    }
    let settled = false;
    const settle = (): boolean => {
      if (settled) return false;
      settled = true;
      state.pending -= 1;
      clearTimeout(timeout);
      return true;
    };
    const retry = document.createElement('script');
    retry.setAttribute(RETRY_ATTRIBUTE, '');
    retry.onload = () => {
      if (!settle()) return;
      state.recoveredUrls.push(src);
      reportRecovered(state);
    };
    retry.onerror = () => {
      settle();
    };
    // A late error after this still reaches onScriptError, where it only joins this report
    const timeout = setTimeout(() => {
      if (settle()) handleFailure(src, { retried: true });
    }, CHUNK_RETRY_TIMEOUT_MS);
    retry.src = src;
    (failed.parentNode ?? document.head).appendChild(retry);
  }, CHUNK_RETRY_DELAY_MS);
}

// ── Background prefetch (TEAM-15666) ──────────────────────────────────────────────────────────

/**
 * Where Next is navigating to, or null when no navigation is in progress. Next loads that page's
 * files with the same <script> elements it uses for a background prefetch, so outside a navigation
 * such a script is a prefetch. Set by the router events registered in
 * {@link initChunkLoadErrorHandler}.
 *
 * A chunk failure during a navigation goes to this destination rather than reloading the page being
 * left: Next hard-navigates there at the same moment, and whichever navigation starts last wins. A
 * reload that won would put the diner back on, say, checkout after paying (TEAM-15666).
 */
let navigationDestination: string | null = null;

/** The options that send a failure during a navigation to the page the diner is going to. */
function navigationTarget(): Pick<FailureOptions, 'destinationUrl'> {
  return navigationDestination === null ? {} : { destinationUrl: navigationDestination };
}

/**
 * A page file Next appended to prefetch another page failed. Next swallows the failure (prefetch in
 * next/dist/client/route-loader.js) and the page the diner is on is unaffected, so it is recorded
 * only, under its own Sentry issue: no reload, the reload allowance is kept, and the load's report
 * is left open for a failure that matters.
 */
function reportPrefetchFailure(src: string): void {
  captureChunkEvent(
    'warning',
    { chunk_source: 'prefetch' },
    [src],
    new Error(`Chunk prefetch failure: ${src}`),
    CHUNK_PREFETCH_FINGERPRINT,
  );
}

/**
 * Where a failed /_next/ <script> came from:
 * - `page`: in the page's HTML (parser-inserted, so `async` is false). The page needs it.
 * - `webpack`: a lazy chunk webpack appended for `import()` (it sets `data-webpack`). Webpack has
 *   already rejected the import when the error arrives, so a retry cannot repair the component.
 * - `next`: appended by Next's route loader (dynamic, so `async` is true) — a navigation or a
 *   background prefetch of another page.
 */
function scriptSource(script: HTMLScriptElement): 'page' | 'webpack' | 'next' {
  if (script.hasAttribute('data-webpack')) return 'webpack';
  return script.async ? 'next' : 'page';
}

/**
 * Capture-phase listener for chunk failures — two shapes:
 *
 *    a) A <script> element fails to load (resource error). `target` is the
 *       HTMLScriptElement and `src` carries the URL. This is the shape that
 *       `X-Content-Type-Options: nosniff` would produce. A file in the page's
 *       HTML is requested once more before the page reloads; a file Next
 *       appended to prefetch another page is only recorded (TEAM-15666).
 *
 *    b) A script loads but the body is HTML (200-with-wrong-content-type after
 *       a deploy), so the browser executes it and throws a SyntaxError such as
 *       "Unexpected token '<'". This fires as an ErrorEvent at `window` with
 *       `target === window` and `filename` pointing to the chunk URL. This is
 *       the COO-WEBAPP-180 population — ~1,000 diners/release (TEAM-14340).
 *       WebKit reports the site root as `filename` instead — see
 *       {@link isRedirectedChunkHtml}. Sentry shows that frame as `app:///`
 *       because its Next.js integration rewrites the origin to `app://`; the
 *       browser still hands this listener the real URL (TEAM-15666). Not
 *       retried: requesting the file again returns the same HTML.
 *
 * NOTE on shape (b): `event.error` is only populated for same-origin scripts.
 * If chunks ever move behind a CDN hostname (via `assetPrefix` or `crossOrigin`
 * on `<NextScript />`), the browser will mute the event to `"Script error."`
 * with `error === null`, and this vector will stop matching.
 */
export function onScriptError(event: Event): void {
  const target = event.target;

  // Shape (a): element-level resource error
  if (target instanceof HTMLScriptElement) {
    const src = target.src || 'unknown';
    if (!src.includes('/_next/')) return;

    // This handler's own retry failed too: reload (TEAM-15666)
    if (target.hasAttribute(RETRY_ATTRIBUTE)) {
      handleFailure(src, { retried: true });
      return;
    }

    const source = scriptSource(target);
    if (source === 'next' && navigationDestination === null) {
      reportPrefetchFailure(src);
      return;
    }
    // A page file this page needs gets one retry first, unless the load is already being handled
    if (source === 'page' && !loadReport) {
      scheduleRetry(target, src);
      return;
    }
    handleFailure(src, navigationTarget());
    return;
  }

  // Shape (b): evaluation error (ErrorEvent at window)
  // Narrowed to SyntaxError — the actual COO-WEBAPP-180 signature ("Unexpected
  // token '<'" when the browser executes an HTML body as JS). Without this
  // guard, any uncaught runtime error from /_next/ code would trigger a reload,
  // discarding client state mid-checkout.
  if (
    event instanceof ErrorEvent &&
    event.error instanceof SyntaxError &&
    !isJsonParseError(event.error) &&
    (event.filename?.includes('/_next/') || isRedirectedChunkHtml(event.filename, event.error))
  ) {
    // Before handleFailure, and for every repeat in the load: Sentry's own onerror event for this
    // error is dropped in beforeSend (TEAM-15331).
    handledChunkErrors.add(event.error);
    // A stale tab's navigation fails this way when the next page's chunk comes back as HTML
    handleFailure(event.filename, { originalError: event.error, ...navigationTarget() });
  }
}

/**
 * A `JSON.parse` failure in Chowly code (e.g. an HTML error page parsed as
 * JSON, TEAM-14935) is a runtime SyntaxError, not a stale script — but it still
 * arrives with a `/_next/` filename: in WebKit 26 and Chromium 141 Sentry wraps
 * timers and event handlers and rethrows from inside the `_app` bundle; Firefox
 * 144 reports the calling file, which in production is also a `/_next/` chunk
 * (TEAM-15273). Every engine names JSON in the message: "… in JSON at
 * position …" / "… is not valid JSON" (Chromium), "JSON Parse error: …"
 * (WebKit), "JSON.parse: …" (Firefox).
 */
function isJsonParseError(error: SyntaxError): boolean {
  return error.message.includes('JSON');
}

/**
 * The WebKit shape of shape (b) (TEAM-15273). WebKit (Safari and every iOS
 * browser) reports a redirected script under the URL it was redirected to, so
 * when a superseded chunk is answered with a redirect to the home page, the
 * SyntaxError is attributed to the site root of this origin (observed in
 * WebKit 26: `https://<host>/`, line 1) instead of the chunk URL.
 * Root-only on purpose — an inline-script error reports the page URL, so
 * matching any same-origin file would spend the tab's single reload on a
 * genuine bug. The one overlap is the home page itself, where an inline script
 * running after this listener with a `'<'` SyntaxError would also match (at
 * most one reload per session). The `'<'` check covers V8/WebKit
 * "Unexpected token '<'" and Firefox "expected expression, got '<'".
 */
function isRedirectedChunkHtml(filename: string, error: SyntaxError): boolean {
  if (!error.message.includes('<')) return false;
  try {
    const url = new URL(filename);
    return url.origin === window.location.origin && url.pathname === '/';
  } catch {
    // Empty or unparsable filename — not the shape we recognize
    return false;
  }
}

/**
 * Register the capture-phase error listener as early as possible — before the
 * page chunk executes — so that a chunk needed for hydration is caught. Called
 * from `instrumentation-client.ts`, which `@sentry/nextjs` injects into the
 * `_app` entry bundle before the module body.
 *
 * NOTE: This cannot catch a failure of the bootstrap files — the webpack
 * runtime, `framework`, `main` and `_app` itself. The listener travels inside
 * `_app`, which only runs once those have loaded, so their error fires with
 * nothing listening: no retry, no reload and no Sentry report (verified on a
 * production build, TEAM-15666). Page and shared chunks, which run after
 * `_app`, are covered.
 */
export function registerEarlyChunkErrorListener(): void {
  window.addEventListener('error', onScriptError, true);
}

/**
 * Detects page-file (chunk) load failures and auto-reloads once per session.
 *
 * Two detection vectors:
 * 1. Capture-phase window 'error' listener — registered early in
 *    `instrumentation-client.ts` via {@link registerEarlyChunkErrorListener}
 *    so it's live before hydration. This catches <script> elements that fail
 *    to load and evaluation errors when HTML is executed as JS.
 * 2. Next.js router 'routeChangeError' — catches dynamic-import failures
 *    during client-side page transitions (ChunkLoadError).
 *
 * Only the first failure per document load is acted on — subsequent script
 * errors in the same load are folded into its single Sentry report (multiple
 * chunks can fail in one page, but they share a single root cause). A
 * sessionStorage counter ensures at most one automatic reload per session;
 * without sessionStorage, a marker in `window.name` allows one reload per path
 * within {@link RELOAD_MARKER_TTL_MS}. If the reload doesn't fix the issue,
 * the error falls through to the existing error boundary.
 *
 * The router events also tell a navigation's page files from a background
 * prefetch's, whose failures never reload, and send a failure during a
 * navigation to its destination (TEAM-15666).
 *
 * Failures of the bootstrap files (webpack runtime, `framework`, `main`,
 * `_app`) are out of reach: see {@link registerEarlyChunkErrorListener}.
 */
export function initChunkLoadErrorHandler(): () => void {
  // Reset per-load report — init is called once per page load (in _app.tsx).
  loadReport = null;
  retryState = { pending: 0, recoveredUrls: [] };
  navigationDestination = null;

  // Tells a navigation's page files from a background prefetch's, and where the navigation goes
  // (see navigationDestination). Signature: (url, { shallow }) — url is the intended destination.
  function onRouteChangeStart(url: string): void {
    navigationDestination = url;
  }
  function onRouteChangeEnd(): void {
    navigationDestination = null;
  }
  Router.events.on('routeChangeStart', onRouteChangeStart);
  Router.events.on('routeChangeComplete', onRouteChangeEnd);

  // 2. Next.js router error handler
  // Signature: (err, url, { shallow }) — url is the intended destination.
  function onRouteChangeError(err: Error & { cancelled?: boolean }, url: string): void {
    navigationDestination = null;
    if (err.cancelled) return;

    const isChunkError =
      err.name === 'ChunkLoadError' ||
      err.message?.includes('Loading chunk') ||
      err.message?.includes('Failed to fetch dynamically imported module');

    if (isChunkError) {
      // Try to extract a URL from the error message; fall back to 'unknown'
      // (avoids leaking the raw error text into the failed_url Sentry context field).
      const urlMatch = err.message.match(/https?:\/\/\S+/);
      handleFailure(urlMatch?.[0] ?? 'unknown', { originalError: err, destinationUrl: url });
    }
  }

  Router.events.on('routeChangeError', onRouteChangeError);

  // The window listener is not removed here: registerEarlyChunkErrorListener owns it for the life of
  // the page. React runs this effect twice in Strict Mode (development), and removing it here would
  // leave the page without one. Adding it back is no answer either: Sentry wraps addEventListener
  // after Sentry.init, so a second registration is a different function and every error would be
  // handled twice (TEAM-15666).
  return () => {
    Router.events.off('routeChangeStart', onRouteChangeStart);
    Router.events.off('routeChangeComplete', onRouteChangeEnd);
    Router.events.off('routeChangeError', onRouteChangeError);
  };
}
